import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Loader2, RefreshCw, Minus, Plus, Trash2, Mail, Phone, FileText, ShoppingBag } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABEL = {
  paid: { label: 'Pagado', className: 'bg-green-100 text-green-700' },
  pending: { label: 'Pendiente pago', className: 'bg-yellow-100 text-yellow-700' },
  failed: { label: 'Fallido', className: 'bg-red-100 text-red-700' },
  refunded: { label: 'Reembolsado', className: 'bg-gray-100 text-gray-700' },
};

export const BonosTab = ({ authHeader, userRole }) => {
  const [purchases, setPurchases] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('paid');
  const [search, setSearch] = useState('');

  const fetchData = async () => {
    if (!authHeader) return;
    setLoading(true);
    try {
      const headers = { Authorization: authHeader };
      const [pRes, cRes] = await Promise.all([
        axios.get(`${API}/admin/bonos/purchases${filter ? `?payment_status=${filter}` : ''}`, { headers }),
        axios.get(`${API}/admin/bonos/catalog`, { headers }),
      ]);
      setPurchases(pRes.data);
      setCatalog(cRes.data);
    } catch {
      toast.error('Error al cargar bonos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authHeader, filter]);

  const consumeSession = async (purchase) => {
    if (!window.confirm(`Descontar 1 sesión del bono ${purchase.code}?`)) return;
    try {
      await axios.post(
        `${API}/admin/bonos/purchases/${purchase.id}/use-session`,
        { date: null, time: '', notes: '' },
        { headers: { Authorization: authHeader } }
      );
      toast.success('Sesión descontada');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const restoreSession = async (purchase) => {
    if (!window.confirm(`Restaurar la última sesión usada del bono ${purchase.code}?`)) return;
    try {
      await axios.post(
        `${API}/admin/bonos/purchases/${purchase.id}/restore-session`,
        {},
        { headers: { Authorization: authHeader } }
      );
      toast.success('Sesión restaurada');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const deletePurchase = async (purchase) => {
    if (!window.confirm(`Eliminar el bono ${purchase.code} de ${purchase.customer_name}? Esta acción es irreversible.`)) return;
    try {
      await axios.delete(`${API}/admin/bonos/purchases/${purchase.id}`, {
        headers: { Authorization: authHeader },
      });
      toast.success('Bono eliminado');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const filtered = purchases.filter((p) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      p.code?.toLowerCase().includes(q) ||
      p.customer_name?.toLowerCase().includes(q) ||
      p.customer_email?.toLowerCase().includes(q) ||
      p.customer_phone?.includes(q)
    );
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          {['paid', 'pending', 'failed', ''].map((s) => (
            <button
              key={s || 'all'}
              onClick={() => setFilter(s)}
              className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                filter === s
                  ? 'bg-halo-brown text-halo-cream'
                  : 'bg-halo-cream text-halo-charcoal hover:bg-halo-brown/10'
              }`}
              data-testid={`bono-filter-${s || 'all'}`}
            >
              {s === '' ? 'Todos' : STATUS_LABEL[s]?.label || s}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Input
            placeholder="Buscar por código, nombre, email..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-64"
            data-testid="bono-search"
          />
          <ManualSaleDialog catalog={catalog} authHeader={authHeader} onCreated={fetchData} />
          <Button onClick={fetchData} variant="outline" size="sm" disabled={loading}>
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>
      </div>

      <div className="bg-halo-surface rounded-xl border border-halo-taupe/10 p-4">
        <p className="text-sm text-halo-text">
          <strong>{filtered.length}</strong> bono{filtered.length !== 1 ? 's' : ''}
          {filter && ` (estado: ${STATUS_LABEL[filter]?.label})`}
          {search && ` que coinciden con "${search}"`}
        </p>
      </div>

      {loading && (
        <div className="flex justify-center py-12">
          <Loader2 className="animate-spin text-halo-brown" size={32} />
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="bg-halo-surface rounded-xl border border-halo-taupe/10 p-10 text-center">
          <p className="text-halo-text">No hay bonos que coincidan.</p>
        </div>
      )}

      <div className="grid gap-4">
        {filtered.map((p) => (
          <BonoRow
            key={p.id}
            purchase={p}
            userRole={userRole}
            onUseSession={() => consumeSession(p)}
            onRestoreSession={() => restoreSession(p)}
            onDelete={() => deletePurchase(p)}
          />
        ))}
      </div>

      {/* Catalog reference at bottom */}
      <div className="mt-8 bg-halo-surface rounded-xl border border-halo-taupe/10 p-5">
        <h3 className="font-heading text-lg text-halo-charcoal mb-3 flex items-center gap-2">
          <FileText size={18} />
          Catálogo de bonos
        </h3>
        <p className="text-xs text-halo-text mb-4">
          Estos son los productos disponibles para compra online en /bonos.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
          {catalog.map((b) => (
            <div
              key={b.id}
              className="flex justify-between items-center bg-halo-cream rounded-lg p-3"
            >
              <div>
                <p className="font-medium text-halo-charcoal">{b.name}</p>
                <p className="text-xs text-halo-text">
                  {b.num_sessions} ses · {b.online_purchase ? '🌐 online' : '🏠 solo centro'}
                </p>
              </div>
              <p className="font-heading text-halo-brown">{b.price}€</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const BonoRow = ({ purchase, userRole, onUseSession, onRestoreSession, onDelete }) => {
  const status = STATUS_LABEL[purchase.payment_status] || { label: purchase.payment_status, className: 'bg-gray-100 text-gray-700' };
  const remaining = purchase.sessions_remaining ?? 0;
  const total = purchase.sessions_total ?? 0;
  const percentUsed = total > 0 ? ((total - remaining) / total) * 100 : 0;

  return (
    <div
      className="bg-halo-surface rounded-xl border border-halo-taupe/10 p-5"
      data-testid={`bono-row-${purchase.code}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono font-bold text-halo-brown text-lg tracking-wider">
              {purchase.code}
            </span>
            <span className={`px-2 py-0.5 rounded-full text-xs ${status.className}`}>
              {status.label}
            </span>
          </div>
          <p className="text-halo-charcoal font-medium">{purchase.bono_name}</p>
          <p className="text-sm text-halo-text">
            {purchase.customer_name} · {purchase.customer_email} · {purchase.customer_phone}
          </p>
        </div>

        <div className="text-right">
          <p className="font-heading text-2xl text-halo-brown">{purchase.total_price}€</p>
          <p className="text-xs text-halo-text">
            Comprado: {purchase.created_at?.slice(0, 10)}
          </p>
          <p className="text-xs text-halo-text">Válido hasta: {purchase.valid_until}</p>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-3">
        <div className="flex justify-between text-xs text-halo-text mb-1">
          <span>
            <strong className="text-halo-charcoal">{remaining}</strong> de {total} sesiones disponibles
          </span>
          <span>{purchase.sessions_used} usadas</span>
        </div>
        <div className="h-2 bg-halo-cream rounded-full overflow-hidden">
          <div
            className="h-full bg-halo-brown transition-all"
            style={{ width: `${percentUsed}%` }}
          />
        </div>
      </div>

      {/* Used sessions list */}
      {purchase.used_sessions?.length > 0 && (
        <details className="mb-3 text-sm">
          <summary className="cursor-pointer text-halo-text hover:text-halo-charcoal">
            Ver historial ({purchase.used_sessions.length})
          </summary>
          <ul className="mt-2 space-y-1 pl-4">
            {purchase.used_sessions.map((s, i) => (
              <li key={`${s.registered_at}-${i}`} className="text-xs text-halo-text">
                {s.date} {s.time && `· ${s.time}`} {s.notes && `· ${s.notes}`}
              </li>
            ))}
          </ul>
        </details>
      )}

      <div className="flex flex-wrap gap-2 pt-3 border-t border-halo-taupe/10">
        {purchase.payment_status === 'paid' && (
          <>
            <Button
              size="sm"
              onClick={onUseSession}
              disabled={remaining <= 0}
              className="bg-halo-brown hover:bg-halo-charcoal text-halo-cream"
              data-testid={`use-session-${purchase.code}`}
            >
              <Minus size={14} className="mr-1" /> Usar 1 sesión
            </Button>
            {purchase.used_sessions?.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={onRestoreSession}
                data-testid={`restore-session-${purchase.code}`}
              >
                <Plus size={14} className="mr-1" /> Restaurar última
              </Button>
            )}
            <a href={`mailto:${purchase.customer_email}`}>
              <Button size="sm" variant="outline">
                <Mail size={14} className="mr-1" /> Email
              </Button>
            </a>
            <a href={`tel:${purchase.customer_phone}`}>
              <Button size="sm" variant="outline">
                <Phone size={14} className="mr-1" /> Llamar
              </Button>
            </a>
            <a href={`${API}/bonos/purchase/${purchase.id}/voucher-pdf`} target="_blank" rel="noopener noreferrer">
              <Button size="sm" variant="outline">
                <FileText size={14} className="mr-1" /> PDF
              </Button>
            </a>
          </>
        )}
        {(userRole === 'admin' || userRole === 'office') && (
          <Button
            size="sm"
            variant="outline"
            onClick={onDelete}
            className="text-red-500 border-red-200 hover:bg-red-50 ml-auto"
            data-testid={`delete-bono-${purchase.code}`}
          >
            <Trash2 size={14} />
          </Button>
        )}
      </div>
    </div>
  );
};

export default BonosTab;


const initialManualSale = {
  bono_id: '',
  customer_name: '',
  customer_email: '',
  customer_phone: '',
  payment_method: 'cash',
  notes: '',
  send_email: true,
};

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Efectivo' },
  { value: 'card_pos', label: 'Tarjeta (datáfono)' },
  { value: 'transfer', label: 'Transferencia' },
  { value: 'other', label: 'Otro' },
];

const ManualSaleDialog = ({ catalog, authHeader, onCreated }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialManualSale);
  const [submitting, setSubmitting] = useState(false);

  const selectedBono = catalog.find((b) => b.id === form.bono_id);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.bono_id || !form.customer_name || !form.customer_phone) {
      toast.error('Producto, nombre y teléfono son obligatorios');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.customer_email) delete payload.customer_email;
      const res = await axios.post(
        `${API}/admin/bonos/purchases/manual`,
        payload,
        { headers: { Authorization: authHeader } }
      );
      toast.success(`Bono creado: ${res.data.code}`);
      setForm(initialManualSale);
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al crear el bono');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size="sm"
          className="bg-halo-brown hover:bg-halo-charcoal text-halo-cream"
          data-testid="manual-sale-trigger"
        >
          <ShoppingBag size={16} className="mr-2" /> Venta manual
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Registrar venta manual</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-4">
          <div>
            <Label>Producto *</Label>
            <Select
              value={form.bono_id}
              onValueChange={(v) => setForm((p) => ({ ...p, bono_id: v }))}
            >
              <SelectTrigger className="mt-1" data-testid="manual-sale-bono">
                <SelectValue placeholder="Selecciona un bono o sesión" />
              </SelectTrigger>
              <SelectContent>
                {catalog.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.name} — {b.price}€
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedBono && (
              <p className="text-xs text-halo-text mt-1">
                {selectedBono.num_sessions} sesion{selectedBono.num_sessions !== 1 ? 'es' : ''} ·{' '}
                Total <strong className="text-halo-brown">{selectedBono.price}€</strong>
              </p>
            )}
          </div>

          <div>
            <Label>Nombre del cliente *</Label>
            <Input
              value={form.customer_name}
              onChange={(e) => setForm((p) => ({ ...p, customer_name: e.target.value }))}
              className="mt-1"
              placeholder="Nombre completo"
              required
              data-testid="manual-sale-name"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Email (opcional)</Label>
              <Input
                type="email"
                value={form.customer_email}
                onChange={(e) =>
                  setForm((p) => ({ ...p, customer_email: e.target.value }))
                }
                className="mt-1"
                placeholder="cliente@email.com"
                data-testid="manual-sale-email"
              />
            </div>
            <div>
              <Label>Teléfono *</Label>
              <Input
                type="tel"
                value={form.customer_phone}
                onChange={(e) =>
                  setForm((p) => ({ ...p, customer_phone: e.target.value }))
                }
                className="mt-1"
                placeholder="678 901 086"
                required
                data-testid="manual-sale-phone"
              />
            </div>
          </div>

          <div>
            <Label>Forma de pago *</Label>
            <Select
              value={form.payment_method}
              onValueChange={(v) => setForm((p) => ({ ...p, payment_method: v }))}
            >
              <SelectTrigger className="mt-1" data-testid="manual-sale-payment">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAYMENT_METHODS.map((m) => (
                  <SelectItem key={m.value} value={m.value}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Notas (opcional)</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
              className="mt-1"
              rows={2}
              placeholder="Comentarios internos..."
            />
          </div>

          {form.customer_email && (
            <label className="flex items-center gap-2 text-sm text-halo-text cursor-pointer">
              <input
                type="checkbox"
                checked={form.send_email}
                onChange={(e) =>
                  setForm((p) => ({ ...p, send_email: e.target.checked }))
                }
                data-testid="manual-sale-send-email"
              />
              Enviar email de confirmación con PDF al cliente
            </label>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={submitting}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-halo-brown hover:bg-halo-charcoal text-halo-cream"
              data-testid="manual-sale-submit"
            >
              {submitting ? 'Creando...' : 'Crear bono'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
